var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as CryptoJS from 'crypto-js';
import localForage from 'localforage';
import * as bcrypt from 'bcryptjs';
class PinCode {
    constructor() {
        this.hashedCode = '';
    }
}
export class Wallet {
    constructor() {
        this.id = Wallet.createId();
        this.name = "";
        this.address = "";
        this.publicKey = "";
        this.encryptedSecret = "";
    }
    static createWithKeys(name, address, publicKey, secretKey, password) {
        const wallet = new Wallet();
        wallet.name = name;
        wallet.address = address;
        wallet.publicKey = publicKey;
        wallet.encryptedSecret = encryptString(secretKey, password);
        return wallet;
    }
    static createId() {
        return new Date().getTime().toString(16) + Math.floor(10000 * Math.random()).toString(16);
    }
    decryptSecret(password) {
        return decryptString(this.encryptedSecret, password);
    }
    encryptSecret(oldPassword, newPassword) {
        const rawSecret = this.decryptSecret(oldPassword);
        if (rawSecret) {
            this.encryptedSecret = encryptString(rawSecret, newPassword);
            return true;
        }
        else {
            return false;
        }
    }
}
class Wallets {
    constructor() {
        this.wallets = [];
        this.activeId = '';
    }
    get(id) {
        const wallet = this.wallets.find((wallet) => wallet.id === id);
        if (wallet) {
            return wallet;
        }
        else {
            return null;
        }
    }
    gets() {
        return this.wallets.slice();
    }
    getActive() {
        return this.get(this.activeId);
    }
    getIndex(id) {
        for (let index = 0; index < this.wallets.length; index++) {
            if (this.wallets[index].id === id) {
                return index;
            }
        }
        return -1;
    }
    getActiveIndex() {
        return this.getIndex(this.activeId);
    }
    setActive(id) {
        this.activeId = id;
    }
    add(wallet) {
        this.wallets.push(wallet);
    }
    delete(id) {
        const index = this.getIndex(id);
        if (index < 0) {
            return;
        }
        this.wallets.splice(index, 1);
        if (this.activeId === id && this.wallets.length > 0) {
            this.activeId = this.wallets[0].id;
        }
    }
    setName(id, name) {
        const wallet = this.get(id);
        if (wallet) {
            wallet.name = name;
        }
    }
}
export class Storage {
    static setup() {
        localForage.config({
            driver: localForage.INDEXEDDB,
            name: 'RaccoonWallet',
            version: 1.0,
            size: 4980736,
            storeName: 'raccoon_wallet_data',
            description: 'Preferences of RaccoonWallet'
        });
    }
    static loadPinCode() {
        return __awaiter(this, void 0, void 0, function* () {
            const storageObject = yield localForage.getItem(this.PIN_CODE_KEY);
            if (storageObject) {
                return Object.assign(new PinCode(), storageObject);
            }
            else {
                return null;
            }
        });
    }
    static savePinCode(pinCode) {
        return __awaiter(this, void 0, void 0, function* () {
            yield localForage.setItem(this.PIN_CODE_KEY, pinCode);
        });
    }
    static loadWallets() {
        return __awaiter(this, void 0, void 0, function* () {
            const storageObject = (yield localForage.getItem(this.WALLETS_KEY)) || "{}";
            const wallets = Object.assign(new Wallets(), storageObject);
            const newWallets = [];
            for (let wallet of wallets.wallets) {
                newWallets.push(Object.assign(new Wallet(), wallet));
            }
            wallets.wallets = newWallets;
            return wallets;
        });
    }
    static saveWallets(wallets) {
        return __awaiter(this, void 0, void 0, function* () {
            yield localForage.setItem(this.WALLETS_KEY, wallets);
        });
    }
}
Storage.PIN_CODE_KEY = "PIN_CODE";
Storage.WALLETS_KEY = "WALLETS";
export class PinCodeHelper {
    static hasSavedCode() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield Storage.loadPinCode()) != null;
        });
    }
    static update(rawCode) {
        return __awaiter(this, void 0, void 0, function* () {
            const hashedCode = yield bcrypt.hash(rawCode, 10);
            const pinCode = new PinCode();
            pinCode.hashedCode = hashedCode;
            yield Storage.savePinCode(pinCode);
        });
    }
    static check(pinCode) {
        return __awaiter(this, void 0, void 0, function* () {
            const savedCode = yield Storage.loadPinCode();
            if (!savedCode) {
                return false;
            }
            else {
                return yield bcrypt.compare(pinCode, savedCode.hashedCode);
            }
        });
    }
}
PinCodeHelper.defaultPin = "093156";
export class WalletsHelper {
    /**
     * Gets wallet.
     * @param {string} id Wallet ID
     * @returns {Promise<Wallet | null>} Wallet which has the given ID or null if there is no wallet which has the ID.
     */
    static get(id) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield Storage.loadWallets()).get(id);
        });
    }
    /**
     * Gets wallets.
     * @returns {Wallet[]} All wallets.
     */
    static gets() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield Storage.loadWallets()).gets();
        });
    }
    /**
     * Gets active wallet.
     * @returns {Wallet | null} Active wallet or null if there is no active wallet.
     */
    static getActive() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield Storage.loadWallets()).getActive();
        });
    }
    /**
     * Sets active wallet.
     * @param {string} id Wallet ID.
     */
    static setActive(id) {
        return __awaiter(this, void 0, void 0, function* () {
            const wallets = yield Storage.loadWallets();
            wallets.setActive(id);
            yield Storage.saveWallets(wallets);
        });
    }
    /**
     * Adds Wallet.
     * @param {Wallet} wallet New Wallet.
     * @param {boolean} activate If the wallet is activated now or not.
     */
    static add(wallet, activate = false) {
        return __awaiter(this, void 0, void 0, function* () {
            const wallets = yield Storage.loadWallets();
            wallets.add(wallet);
            if (activate) {
                wallets.setActive(wallet.id);
            }
            yield Storage.saveWallets(wallets);
        });
    }
    /**
     * Deletes wallet.
     * @param {string} id Wallet ID
     */
    static delete(id) {
        return __awaiter(this, void 0, void 0, function* () {
            const wallets = yield Storage.loadWallets();
            const wallet = wallets.get(id);
            if (wallet == null) {
                return;
            }
            wallets.delete(id);
            yield Storage.saveWallets(wallets);
        });
    }
    static decryptKey(id, password) {
        return __awaiter(this, void 0, void 0, function* () {
            const wallet = yield this.get(id);
            if (!wallet) {
                return null;
            }
            return wallet.decryptSecret(password);
        });
    }
    static createWithKeys(name, address, publicKey, secretKey, password) {
        return __awaiter(this, void 0, void 0, function* () {
            let wallet;
            do {
                wallet = Wallet.createWithKeys(name, address, publicKey, secretKey, password);
            } while ((yield this.get(wallet.id)) != null);
            return wallet;
        });
    }
    /**
     * Encrypts all registered wallets again.
     * @param {string} oldPassword Current password for encryption.
     * @param {string} newPassword New password for encryption.
     */
    static encryptWallets(oldPassword, newPassword) {
        return __awaiter(this, void 0, void 0, function* () {
            const wallets = yield Storage.loadWallets();
            for (const wallet of wallets.gets()) {
                if (!wallet.encryptSecret(oldPassword, newPassword)) {
                    return false;
                }
            }
            yield Storage.saveWallets(wallets);
            return true;
        });
    }
    /**
     * Gets wallet name.
     * @param {string} id Wallet ID.
     * @returns {string} Wallet Name.
     */
    static getName(id) {
        return __awaiter(this, void 0, void 0, function* () {
            const wallet = yield this.get(id);
            if (!wallet) {
                return '';
            }
            return wallet.name;
        });
    }
    /**
     * Sets wallet name.
     * @param {string} id Wallet ID.
     * @param {string} name New wallet ID.
     */
    static setName(id, name) {
        return __awaiter(this, void 0, void 0, function* () {
            const wallets = yield Storage.loadWallets();
            wallets.setName(id, name);
            yield Storage.saveWallets(wallets);
        });
    }
}
function encryptString(message, key) {
    return CryptoJS.AES.encrypt(message, key).toString();
}
function decryptString(encrypted, key) {
    const decrypted = CryptoJS.AES.decrypt(encrypted, key);
    try {
        return decrypted.toString(CryptoJS.enc.Utf8);
    }
    catch (e) {
        return null;
    }
}



//////////////////
// WEBPACK FOOTER
// ./src/lib/storage.ts
// module id = 18
// module chunks = 1